/**
 * SceneTab - Professional Skybox Editor with 360° Viewer
 * 
 * Features:
 * - Instant image preview + immersive 360° mode using AssetViewerWithSkybox
 * - Prominent skybox ID and remix ID display
 * - Style selector with preview
 * - Generation with progress tracking
 * 
 * Data Source: skybox_glb_urls collection (NEW Firestore schema)
 * Skybox GLB URLs are now stored in the skybox_glb_urls collection
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { Scene, SkyboxGLBUrl } from '../../../types/curriculum';
import { skyboxApiService } from '../../../services/skyboxApiService';
import { getTopicSkybox, getSkyboxGLBUrls, SkyboxData } from '../../../lib/firestore/queries';
import { AssetViewerWithSkybox } from '../../AssetViewerWithSkybox';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { uploadSkyboxImage } from '../../../services/skyboxImageService';
import { generateStreetViewSkybox } from '../../../services/streetViewSkyboxService';
import { fetchPlaceSuggestions, fetchPlaceDetails, StreetViewPlacePrediction } from '../../../services/streetViewPlacesService';
import { StreetViewMapPicker } from '../StreetViewMapPicker';
import GoogleEarthGlobePicker, { type GlobeLocationSelection } from '../GoogleEarthGlobePicker';
import type { StreetViewCoverageResult } from '../../../services/streetViewTourService';
import { Button } from '@/Components/ui/button';
import { useAuth } from '../../../contexts/AuthContext';
import {
  Sparkles,
  Image,
  RefreshCw,
  ExternalLink,
  Loader2,
  Play,
  ChevronDown,
  Minimize2,
  Copy,
  Check,
  Info,
  Palette,
  Zap,
  Globe,
  ImageIcon,
  Upload,
  X,
} from 'lucide-react';

interface SkyboxStyle {
  id: number | string;
  name: string;
  image?: string;
  image_jpg?: string;
  preview_url?: string;
  description?: string;
  model?: string;
}

interface SceneTabProps {
  sceneFormState: Partial<Scene>;
  onSceneChange: (field: keyof Scene, value: unknown) => void;
  isReadOnly: boolean;
  chapterId: string;
  versionId: string;
  topicId: string;
}

export const SceneTab = ({
  sceneFormState,
  onSceneChange,
  isReadOnly,
  chapterId,
  versionId,
  topicId,
}: SceneTabProps) => {
  // Skybox data state
  const [skyboxData, setSkyboxData] = useState<SkyboxData | null>(null);
  const [loadingSkybox, setLoadingSkybox] = useState(true);
  
  // Skybox GLB URLs from new collection
  const [skyboxGLBUrls, setSkyboxGLBUrls] = useState<SkyboxGLBUrl[]>([]);
  const [selectedSkyboxGLB, setSelectedSkyboxGLB] = useState<SkyboxGLBUrl | null>(null);
  
  // Skybox styles state
  const [skyboxStyles, setSkyboxStyles] = useState<SkyboxStyle[]>([]);
  const [selectedStyle, setSelectedStyle] = useState<SkyboxStyle | null>(null);
  const [loadingStyles, setLoadingStyles] = useState(true);
  
  // Generation state
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  
  // UI state
  const [showFullPreview, setShowFullPreview] = useState(true);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showInfoPanel, setShowInfoPanel] = useState(true);
  const [view360, setView360] = useState(true); // Default to 360° view like /main
  const [imageLoaded, setImageLoaded] = useState(false);
  const [viewer360Error, setViewer360Error] = useState(false);
  
  // Skybox source: upload (equirectangular / AI) vs Google Street View
  const [skyboxSource, setSkyboxSource] = useState<'upload' | 'streetview'>('upload');
  const [streetViewMode, setStreetViewMode] = useState<'location' | 'pano'>('location');
  const [streetViewLat, setStreetViewLat] = useState<string>('');
  const [streetViewLng, setStreetViewLng] = useState<string>('');
  const [streetViewHeading, setStreetViewHeading] = useState<number>(0);
  const [streetViewPitch, setStreetViewPitch] = useState<number>(0);
  const [streetViewFov, setStreetViewFov] = useState<number>(90);
  const [streetViewSize, setStreetViewSize] = useState<string>('1024x1024');
  const [fetchingStreetView, setFetchingStreetView] = useState(false);
  const [streetViewPlaceQuery, setStreetViewPlaceQuery] = useState('');
  const [streetViewSuggestions, setStreetViewSuggestions] = useState<StreetViewPlacePrediction[]>([]);
  const [streetViewSuggestionsOpen, setStreetViewSuggestionsOpen] = useState(false);
  const [streetViewSelectedPlaceName, setStreetViewSelectedPlaceName] = useState<string | null>(null);
  const [streetViewMapOpen, setStreetViewMapOpen] = useState(false);
  const [streetViewPanoId, setStreetViewPanoId] = useState('');
  /** A place chosen on the globe, waiting for "Use as topic skybox". */
  const [globeSelection, setGlobeSelection] = useState<
    (GlobeLocationSelection & { coverage: StreetViewCoverageResult }) | null
  >(null);
  const [streetViewAdvancedOpen, setStreetViewAdvancedOpen] = useState(false);
  
  // Skybox image upload state
  const [uploadingSkybox, setUploadingSkybox] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  
  // Prompt state (local editing)
  const [localPrompt, setLocalPrompt] = useState(sceneFormState.in3d_prompt || '');
  
  // Sync local prompt with form state
  useEffect(() => {
    setLocalPrompt(sceneFormState.in3d_prompt || '');
  }, [sceneFormState.in3d_prompt]);
  
  // Load skybox data from Firestore
  // Priority: skybox_glb_urls collection (NEW) -> legacy skybox data
  useEffect(() => {
    const loadSkyboxData = async () => {
      if (!chapterId || !topicId) return;
      
      setLoadingSkybox(true);
      setImageLoaded(false);
      setViewer360Error(false);
      
      try {
        // First, try to load from skybox_glb_urls collection (NEW schema)
        console.log('🔍 Fetching skybox GLB URLs from skybox_glb_urls collection...');
        const glbUrls = await getSkyboxGLBUrls(chapterId, topicId);
        setSkyboxGLBUrls(glbUrls);
        
        if (glbUrls.length > 0) {
          // Use the first skybox GLB URL
          const primarySkybox = glbUrls[0];
          setSelectedSkyboxGLB(primarySkybox);
          
          console.log('✅ Using skybox from skybox_glb_urls collection:', primarySkybox);
          
          // Convert to SkyboxData format for compatibility
          setSkyboxData({
            id: primarySkybox.skybox_id || primarySkybox.id,
            imageUrl: primarySkybox.preview_url || primarySkybox.glb_url,
            file_url: primarySkybox.glb_url,
            promptUsed: primarySkybox.prompt_used || '',
            styleId: primarySkybox.style_id,
            styleName: primarySkybox.style_name,
            status: primarySkybox.status,
          });
          setImageLoadError(false);
        } else {
          // Fallback: Try legacy skybox loading
          console.log('ℹ️ No skybox GLB URLs found, trying legacy source...');
          const data = await getTopicSkybox(chapterId, topicId);
          console.log('🖼️ Loaded legacy skybox data:', data);
          setSkyboxData(data);
          setImageLoadError(false);
        }
      } catch (error) {
        console.error('Error loading skybox:', error);
      } finally {
        setLoadingSkybox(false);
      }
    };
    
    loadSkyboxData();
  }, [chapterId, topicId]);
  
  // Fetch skybox styles on mount
  useEffect(() => {
    const fetchStyles = async () => {
      setLoadingStyles(true);
      try {
        const response = await skyboxApiService.getStyles(1, 100);
        if (response.success && response.data) {
          setSkyboxStyles(response.data);
          if (response.data.length > 0 && !selectedStyle) {
            const defaultStyle = response.data.find((s: SkyboxStyle) => {
              const name = s.name?.toLowerCase() || '';
              return name.includes('ethereal') && name.includes('fantasy');
            }) || response.data[0];
            setSelectedStyle(defaultStyle);
          }
        }
      } catch (error) {
        console.error('Error fetching skybox styles:', error);
      } finally {
        setLoadingStyles(false);
      }
    };
    
    fetchStyles();
  }, []);

  const handleStyleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const styleId = e.target.value;
    const style = skyboxStyles.find(s => String(s.id) === styleId);
    if (style) {
      setSelectedStyle(style);
    }
  };
  
  const handlePromptChange = (value: string) => {
    setLocalPrompt(value);
    onSceneChange('in3d_prompt', value);
  };
  
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(label);
    toast.success(`${label} copied to clipboard`);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Handle skybox image upload/replacement
  const handleSkyboxImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!user?.uid) {
      toast.error('User not authenticated');
      return;
    }

    if (isReadOnly) {
      toast.error('Read-only mode: Cannot replace skybox');
      return;
    }

    setUploadingSkybox(true);
    setUploadProgress(10);

    try {
      // Validate and upload
      setUploadProgress(20);
      const result = await uploadSkyboxImage({
        chapterId,
        topicId,
        file,
        userId: user.uid,
        existingSkyboxId: selectedSkyboxGLB?.skybox_id || selectedSkyboxGLB?.id,
      });
      
      setUploadProgress(80);

      if (!result.success) {
        toast.error(result.error || 'Failed to upload skybox image');
        return;
      }

      setUploadProgress(90);
      
      // Show warning if aspect ratio is not ideal (non-blocking)
      if (result.warning) {
        toast.warning(result.warning, { autoClose: 5000 });
      }
      
      toast.success('Skybox image replaced successfully!');

      // Reload skybox data to reflect the change
      const glbUrls = await getSkyboxGLBUrls(chapterId, topicId);
      setUploadProgress(100);
      setSkyboxGLBUrls(glbUrls);
      
      if (glbUrls.length > 0) {
        const primarySkybox = glbUrls[0];
        setSelectedSkyboxGLB(primarySkybox);
        setSkyboxData({
          id: primarySkybox.skybox_id || primarySkybox.id,
          imageUrl: primarySkybox.preview_url || primarySkybox.glb_url,
          file_url: primarySkybox.glb_url,
          promptUsed: primarySkybox.prompt_used || '',
          styleId: primarySkybox.style_id,
          styleName: primarySkybox.style_name,
          status: primarySkybox.status,
        });
        const skyboxId = primarySkybox.skybox_id || primarySkybox.id;
        const skyboxUrl = primarySkybox.glb_url || primarySkybox.preview_url || '';
        onSceneChange('skybox_id', skyboxId);
        onSceneChange('skybox_url', skyboxUrl);
      }

      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading skybox image:', error);
      toast.error('Failed to upload skybox image');
    } finally {
      setUploadingSkybox(false);
      setUploadProgress(0);
    }
  };

  // Trigger file input click
  const triggerFileUpload = () => {
    if (isReadOnly) {
      toast.error('Read-only mode: Cannot replace skybox');
      return;
    }
    fileInputRef.current?.click();
  };

  // Fetch Google Street View and set as skybox. `target` comes from the globe;
  // without it the advanced fields (pano ID, or coordinates) are used.
  const applyStreetViewSkybox = async (
    target?: { panoId: string } | { location: { lat: number; lng: number } }
  ) => {
    if (!user?.uid) {
      toast.error('User not authenticated');
      return;
    }
    if (isReadOnly) {
      toast.error('Read-only mode: Cannot set skybox');
      return;
    }
    if (!chapterId?.trim() || !topicId?.trim()) {
      toast.error('Chapter and topic are required. Save or select a topic first.');
      return;
    }

    let params: any = {
      heading: streetViewHeading,
      pitch: streetViewPitch,
      fov: streetViewFov,
      size: streetViewSize,
      chapterId,
      topicId,
      userId: user.uid,
    };

    if (target) {
      params = { ...params, ...target };
    } else if (streetViewMode === 'pano') {
      const pano = streetViewPanoId.trim();
      if (!pano) {
        toast.error('Please enter a panorama ID (pano)');
        return;
      }
      params = { ...params, panoId: pano };
    } else {
      const lat = parseFloat(streetViewLat);
      const lng = parseFloat(streetViewLng);
      if (Number.isNaN(lat) || Number.isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        toast.error('Please select a valid location (via search, map, or coordinates)');
        return;
      }
      params = { ...params, location: { lat, lng } };
    }
    setFetchingStreetView(true);
    try {
      const result = await generateStreetViewSkybox(params);
      if (result.success) {
        onSceneChange('skybox_id', result.skyboxId);
        onSceneChange('skybox_url', result.imageUrl);
        setSkyboxData({
          id: result.skyboxId,
          imageUrl: result.imageUrl,
          file_url: result.imageUrl,
          promptUsed: 'Google Street View',
          styleId: undefined,
          styleName: undefined,
          status: 'complete',
        });
        setImageLoadError(false);
        setImageLoaded(false);
        setViewer360Error(false);
        const glbUrls = await getSkyboxGLBUrls(chapterId, topicId);
        setSkyboxGLBUrls(glbUrls);
        if (glbUrls.length > 0) {
          setSelectedSkyboxGLB(glbUrls[0]);
        }
        setGlobeSelection(null);
        toast.success('Street View skybox set successfully');
      } else {
        toast.error(result.error || 'Failed to fetch Street View skybox');
      }
    } catch (err) {
      console.error('Street View skybox error:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to fetch Street View skybox');
    } finally {
      setFetchingStreetView(false);
    }
  };
  
  // Generate skybox
  const handleGenerateSkybox = useCallback(async () => {
    if (!localPrompt.trim()) {
      toast.error('Please enter a scene prompt first');
      return;
    }
    
    if (!selectedStyle) {
      toast.error('Please select a skybox style');
      return;
    }
    
    setGenerating(true);
    setGenerationProgress(10);
    
    try {
      const response = await skyboxApiService.generateSkybox({
        prompt: localPrompt.trim(),
        style_id: selectedStyle.id,
      });
      
      const generationId = response.data?.generationId || response.data?.id;
      if (!response.success || !generationId) {
        throw new Error('Failed to start skybox generation');
      }
      
      setGenerationProgress(30);
      
      let attempts = 0;
      const maxAttempts = 90;
      
      while (attempts < maxAttempts) {
        attempts++;
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        try {
          const status = await skyboxApiService.getSkyboxStatus(generationId.toString());
          const normalizedStatus = status.data?.status?.toLowerCase();
          
          if (normalizedStatus === 'complete' || normalizedStatus === 'completed' || status.data?.file_url) {
            const skyboxUrl = status.data.file_url || status.data.image || status.data.imageUrl;
            
            onSceneChange('skybox_id', generationId.toString());
            onSceneChange('skybox_url', skyboxUrl);
            
            // Save to skybox_glb_urls collection (NEW schema)
            try {
              const newSkyboxGLB: Omit<SkyboxGLBUrl, 'id'> = {
                chapter_id: chapterId,
                topic_id: topicId,
                skybox_id: generationId.toString(),
                glb_url: skyboxUrl,
                preview_url: skyboxUrl,
                prompt_used: localPrompt,
                style_id: typeof selectedStyle.id === 'number' ? selectedStyle.id : parseInt(selectedStyle.id as string),
                style_name: selectedStyle.name,
                status: 'complete',
                created_at: new Date().toISOString(),
              };
              
              await addDoc(collection(db, 'skybox_glb_urls'), {
                ...newSkyboxGLB,
                created_at: serverTimestamp(),
              });
              console.log('✅ Saved to skybox_glb_urls collection');
            } catch (saveError) {
              console.warn('Could not save to skybox_glb_urls collection:', saveError);
            }
            
            setSkyboxData({
              id: generationId.toString(),
              imageUrl: skyboxUrl,
              file_url: skyboxUrl,
              promptUsed: localPrompt,
              styleId: typeof selectedStyle.id === 'number' ? selectedStyle.id : parseInt(selectedStyle.id as string),
              styleName: selectedStyle.name,
              status: 'complete',
              obfuscated_id: status.data.obfuscated_id,
              remix_id: status.data.remix_id || status.data.obfuscated_id,
            });
            
            setGenerationProgress(100);
            setImageLoadError(false);
            setImageLoaded(false);
            setViewer360Error(false);
            toast.success('Skybox generated successfully!');
            setGenerating(false);
            setGenerationProgress(0);
            return;
          } else if (normalizedStatus === 'error' || normalizedStatus === 'failed' || normalizedStatus === 'abort') {
            throw new Error(status.data?.error_message || 'Skybox generation failed');
          }
          
          const progress = Math.min(30 + (attempts / maxAttempts) * 60, 90);
          setGenerationProgress(progress);
        } catch (pollError: unknown) {
          console.error('Poll error:', pollError);
          if (pollError instanceof Error && pollError.message?.includes('not found')) {
            throw pollError;
          }
        }
      }
      
      throw new Error('Generation timed out. Please try again.');
    } catch (error: unknown) {
      console.error('Skybox generation error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to generate skybox');
    } finally {
      setGenerating(false);
      setGenerationProgress(0);
    }
  }, [localPrompt, selectedStyle, onSceneChange]);

  // Current skybox data
  const currentSkyboxUrl = skyboxData?.imageUrl || skyboxData?.file_url || sceneFormState.skybox_url;
  const currentSkyboxId = skyboxData?.id || sceneFormState.skybox_id;
  const currentRemixId = skyboxData?.remix_id || skyboxData?.obfuscated_id || (sceneFormState as Record<string, unknown>).skybox_remix_id as string | undefined;
  const currentPrompt = skyboxData?.promptUsed || sceneFormState.in3d_prompt;
  
  const isValidSkyboxUrl = currentSkyboxUrl && 
    typeof currentSkyboxUrl === 'string' && 
    currentSkyboxUrl.startsWith('http') &&
    !imageLoadError;

  return (
    <div className="h-full flex flex-col overflow-hidden bg-background">
      {/* Full-screen Background */}
      <div className="relative flex-1 min-h-0">
        {/* Loading state for data */}
        {loadingSkybox ? (
          <div className="absolute inset-0 flex items-center justify-center bg-background">
            <div className="text-center">
              <div className="relative w-16 h-16 mx-auto mb-4">
                <div className="absolute inset-0 rounded-full border-2 border-primary/20" />
                <div className="absolute inset-0 rounded-full border-2 border-primary border-t-transparent animate-spin" />
              </div>
              <p className="text-sm text-muted-foreground">Loading scene data...</p>
            </div>
          </div>
        ) : isValidSkyboxUrl && showFullPreview ? (
          <>
            {/* 360° Viewer using AssetViewerWithSkybox (same as /main page) */}
            {view360 && !viewer360Error ? (
              <div className="absolute inset-0">
                <AssetViewerWithSkybox
                  assetUrl="" 
                  skyboxImageUrl={currentSkyboxUrl!}
                  className="w-full h-full"
                  autoRotate={true}
                  onLoad={() => {
                    console.log('✅ 360° viewer loaded');
                    setImageLoaded(true);
                  }}
                  onError={(error) => {
                    console.error('❌ 360° viewer error:', error);
                    setViewer360Error(true);
                    setView360(false);
                  }}
                />
              </div>
            ) : (
              /* 2D Image Fallback */
              <div className="absolute inset-0">
                <img
                  src={currentSkyboxUrl}
                  alt="Skybox preview"
                  className="w-full h-full object-cover"
                  onLoad={() => setImageLoaded(true)}
                  onError={() => setImageLoadError(true)}
                />
                {/* Gradient overlays for 2D view */}
                <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/30" />
                <div className="absolute inset-0 bg-gradient-to-r from-background/40 via-transparent to-background/40" />
                {/* Vignette */}
                <div className="absolute inset-0" style={{
                  background: 'radial-gradient(ellipse at center, transparent 40%, rgba(5,8,16,0.6) 100%)'
                }} />
              </div>
            )}
          </>
        ) : (
          /* No Skybox State */
          <div className="absolute inset-0 bg-background">
            {/* Animated Grid Background */}
            <div className="absolute inset-0 opacity-30">
              <div className="absolute inset-0" style={{
                backgroundImage: `
                  linear-gradient(rgba(6,182,212,0.03) 1px, transparent 1px),
                  linear-gradient(90deg, rgba(6,182,212,0.03) 1px, transparent 1px)
                `,
                backgroundSize: '50px 50px',
              }} />
            </div>
            {/* Floating Orbs */}
            <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/5 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-violet-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
            
            {/* No skybox message */}
            {!generating && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center p-8 max-w-lg">
                  <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-violet-500/20 flex items-center justify-center border border-white/10">
                    <Image className="w-12 h-12 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground mb-3">Create Your Environment</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Generate immersive 360° skybox environments using BlockadeLabs AI. 
                    Describe your scene and select a style to get started.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Gradient Overlays for controls readability (only for 360 view) */}
        {isValidSkyboxUrl && showFullPreview && view360 && !viewer360Error && (
          <>
            <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/60 to-transparent pointer-events-none z-10" />
            <div className="absolute inset-x-0 bottom-0 h-72 bg-gradient-to-t from-background via-background/90 to-transparent pointer-events-none z-10" />
          </>
        )}
        
        {/* ==================== TOP BAR ==================== */}
        <div className="absolute top-0 left-0 right-0 z-30">
          <div className="flex items-start justify-between p-4 gap-4">
            
            {/* LEFT: Skybox Info Panel */}
            {currentSkyboxId && showInfoPanel && (
              <div className="bg-black/80 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden shadow-2xl min-w-[280px]">
                {/* Header */}
                <div className="px-4 py-3 bg-gradient-to-r from-cyan-500/10 to-violet-500/10 border-b border-white/5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-foreground/80 uppercase tracking-wider flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
                      Environment Active
                    </span>
                    <button
                      onClick={() => setShowInfoPanel(false)}
                      className="text-foreground/40 hover:text-foreground/80 transition-colors"
                    >
                      <Minimize2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-4 space-y-3">
                  {/* Skybox ID */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[10px] text-primary/80 uppercase tracking-wider font-medium">Skybox ID</span>
                      <button
                        onClick={() => copyToClipboard(currentSkyboxId, 'Skybox ID')}
                        className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-foreground/40 hover:text-foreground transition-all"
                      >
                        {copiedId === 'Skybox ID' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                    <div className="px-3 py-2 bg-muted rounded-lg border border-primary/20">
                      <p className="text-xs text-foreground font-mono truncate">{currentSkyboxId}</p>
                    </div>
                  </div>
                  
                  {/* Remix ID */}
                  {currentRemixId && (
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10px] text-violet-400/80 uppercase tracking-wider font-medium">Remix ID</span>
                        <button
                          onClick={() => copyToClipboard(currentRemixId, 'Remix ID')}
                          className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-foreground/40 hover:text-foreground transition-all"
                        >
                          {copiedId === 'Remix ID' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                      <div className="px-3 py-2 bg-muted rounded-lg border border-violet-500/20">
                        <p className="text-xs text-violet-300 font-mono truncate">{currentRemixId}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Style */}
                  {skyboxData?.styleName && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                      <Palette className="w-4 h-4 text-emerald-400" />
                      <span className="text-xs text-emerald-300">{skyboxData.styleName}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Collapsed Info Button */}
            {currentSkyboxId && !showInfoPanel && (
              <button
                onClick={() => setShowInfoPanel(true)}
                className="p-3 bg-black/60 backdrop-blur-sm rounded-xl border border-white/10 hover:bg-black/80 transition-all"
              >
                <Info className="w-5 h-5 text-primary" />
              </button>
            )}
            
            {/* RIGHT: View Controls */}
            <div className="flex items-center gap-2">
              {isValidSkyboxUrl && (
                <>
                  {/* Replace Skybox Button */}
                  {!isReadOnly && (
                    <div className="relative">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleSkyboxImageUpload}
                        className="hidden"
                        disabled={uploadingSkybox}
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingSkybox || isReadOnly}
                        className="p-2.5 bg-amber-500/10 hover:bg-amber-500/20 rounded-xl backdrop-blur-sm border border-amber-500/30 text-amber-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        title="Replace skybox image (equirectangular 2:1 recommended, but any image will work)"
                      >
                        {uploadingSkybox ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Upload className="w-5 h-5" />
                        )}
                      </button>
                      {uploadingSkybox && uploadProgress > 0 && (
                        <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs text-amber-400 bg-black/80 px-2 py-1 rounded z-50">
                          {Math.round(uploadProgress)}%
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* View Mode Toggle */}
                  <div className="flex items-center bg-black/60 backdrop-blur-sm rounded-xl border border-white/10 p-1">
                    <button
                      onClick={() => {
                        setView360(false);
                        setViewer360Error(false);
                      }}
                      className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all
                               ${!view360 
                                 ? 'bg-primary/20 text-primary' 
                                 : 'text-foreground/60 hover:text-foreground'
                               }`}
                    >
                      <ImageIcon className="w-4 h-4" />
                      2D
                    </button>
                    <button
                      onClick={() => {
                        setView360(true);
                        setViewer360Error(false);
                      }}
                      className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all
                               ${view360 && !viewer360Error
                                 ? 'bg-primary/20 text-primary' 
                                 : 'text-foreground/60 hover:text-foreground'
                               }`}
                    >
                      <Globe className="w-4 h-4" />
                      360°
                    </button>
                  </div>
                  
                  <button
                    onClick={() => setShowFullPreview(!showFullPreview)}
                    className="p-2.5 bg-black/50 hover:bg-black/70 rounded-xl backdrop-blur-sm border border-white/10 text-foreground/80 transition-all"
                  >
                    {showFullPreview ? <Minimize2 className="w-5 h-5" /> : <Image className="w-5 h-5" />}
                  </button>
                  <a
                    href={currentSkyboxUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2.5 bg-black/50 hover:bg-black/70 rounded-xl backdrop-blur-sm border border-white/10 text-foreground/80 transition-all"
                  >
                    <ExternalLink className="w-5 h-5" />
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* Interaction hint for 360 view */}
        {isValidSkyboxUrl && view360 && !viewer360Error && imageLoaded && (
          <div className="absolute bottom-80 left-1/2 -translate-x-1/2 z-20 pointer-events-none">
            <div className="px-4 py-2 bg-black/60 backdrop-blur-sm rounded-full text-xs text-foreground/60 flex items-center gap-2 animate-pulse">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
              </svg>
              Drag to look around • Scroll to zoom
            </div>
          </div>
        )}
        
        {/* ==================== GENERATION PROGRESS ==================== */}
        {generating && (
          <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/85 backdrop-blur-md">
            <div className="text-center p-10 bg-gradient-to-b from-slate-900/90 to-slate-800/90 rounded-3xl border border-primary/20 shadow-2xl shadow-cyan-500/10 max-w-md">
              <div className="relative w-28 h-28 mx-auto mb-8">
                {/* Outer glow */}
                <div className="absolute inset-0 rounded-full bg-primary/20 blur-xl animate-pulse" />
                {/* Track */}
                <div className="absolute inset-0 rounded-full border-4 border-border" />
                {/* Progress */}
                <svg className="absolute inset-0 w-full h-full -rotate-90">
                  <circle
                    cx="56"
                    cy="56"
                    r="52"
                    fill="none"
                    stroke="url(#progressGradient)"
                    strokeWidth="8"
                    strokeLinecap="round"
                    strokeDasharray={`${generationProgress * 3.27} 327`}
                    className="transition-all duration-500"
                  />
                  <defs>
                    <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#06b6d4" />
                      <stop offset="100%" stopColor="#8b5cf6" />
                    </linearGradient>
                  </defs>
                </svg>
                {/* Percentage */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-violet-400 bg-clip-text text-transparent">
                    {Math.round(generationProgress)}%
                  </span>
                </div>
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">Generating Environment</h3>
              <p className="text-muted-foreground text-sm mb-6">Creating your immersive 360° skybox...</p>
              <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <Zap className="w-3.5 h-3.5 text-primary" />
                Powered by BlockadeLabs AI
              </div>
            </div>
          </div>
        )}
        
        {/* ==================== BOTTOM EDITOR PANEL ==================== */}
        <div className="absolute bottom-0 left-0 right-0 z-20">
          <div className="pt-12 pb-6 px-6">
            <div className="max-w-5xl mx-auto">
              {/* Current Prompt Display */}
              {currentPrompt && currentSkyboxUrl && (
                <div className="mb-4 p-4 bg-black/60 backdrop-blur-md rounded-2xl border border-border">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Sparkles className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Active Scene Prompt</span>
                      <p className="text-sm text-foreground mt-1 leading-relaxed">{currentPrompt}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Skybox source toggle */}
              <div className="mb-4 flex items-center gap-3">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Skybox source</span>
                <div className="flex rounded-xl border border-border bg-muted/50 p-1">
                  <button
                    type="button"
                    onClick={() => setSkyboxSource('upload')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${skyboxSource === 'upload' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    Upload / Generate
                  </button>
                  <button
                    type="button"
                    onClick={() => setSkyboxSource('streetview')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-2 ${skyboxSource === 'streetview' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    <Globe className="w-4 h-4" />
                    Google Street View
                  </button>
                </div>
              </div>

              {skyboxSource === 'streetview' ? (
                /* Google Street View panel */
                <div className="bg-black/60 backdrop-blur-md rounded-2xl border border-border p-5 space-y-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <Globe className="w-5 h-5 text-primary" />
                    Street View source
                  </div>
                  {/*
                    The same globe the Street View lesson creator uses: curated
                    destinations, search, and a coverage check, so only places
                    that actually have Street View can be chosen.
                  */}
                  <GoogleEarthGlobePicker
                    disabled={isReadOnly || fetchingStreetView}
                    onSelectLocation={(selection) => setGlobeSelection(selection)}
                    onFallback={() => setStreetViewAdvancedOpen(true)}
                  />
                  {globeSelection && (
                    <div className="rounded-xl border border-primary bg-[color-mix(in_oklch,var(--primary)_10%,transparent)] p-4 space-y-3">
                      <div>
                        <div className="text-sm font-semibold text-foreground">{globeSelection.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {globeSelection.address || `${globeSelection.lat.toFixed(5)}, ${globeSelection.lng.toFixed(5)}`}
                          {globeSelection.coverage?.date ? ` · Street View ${globeSelection.coverage.date}` : ''}
                        </div>
                        {globeSelection.coverage?.copyright && (
                          <div className="text-[11px] text-muted-foreground mt-1">{globeSelection.coverage.copyright}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          disabled={isReadOnly || fetchingStreetView}
                          onClick={() =>
                            void applyStreetViewSkybox(
                              globeSelection.coverage?.panoId
                                ? { panoId: globeSelection.coverage.panoId }
                                : {
                                    location: {
                                      lat: globeSelection.coverage?.lat ?? globeSelection.lat,
                                      lng: globeSelection.coverage?.lng ?? globeSelection.lng,
                                    },
                                  }
                            )
                          }
                        >
                          {fetchingStreetView ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                          Use as topic skybox
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setGlobeSelection(null)} disabled={fetchingStreetView}>
                          Clear
                        </Button>
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setStreetViewAdvancedOpen((v) => !v)}
                    className="text-xs font-medium text-muted-foreground hover:text-foreground"
                  >
                    {streetViewAdvancedOpen ? '▾' : '▸'} Advanced: coordinates, map or pano ID
                  </button>
                  {streetViewAdvancedOpen && (<>

                  {/* Mode toggle: location vs pano */}
                  <div className="inline-flex rounded-xl border border-border bg-muted/50 p-1 mb-2">
                    <button
                      type="button"
                      onClick={() => setStreetViewMode('location')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                        streetViewMode === 'location'
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      By place / map
                    </button>
                    <button
                      type="button"
                      onClick={() => setStreetViewMode('pano')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                        streetViewMode === 'pano'
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      By panorama ID
                    </button>
                  </div>

                  {streetViewMode === 'location' ? (
                    <>
                      {/* Place search + map refine */}
                      <div className="space-y-2">
                        <label className="block text-xs text-muted-foreground mb-1">Search location</label>
                        <div className="relative">
                          <input
                            type="text"
                            value={streetViewPlaceQuery}
                            onChange={async (e) => {
                              const value = e.target.value;
                              setStreetViewPlaceQuery(value);
                              if (!value.trim()) {
                                setStreetViewSuggestions([]);
                                setStreetViewSuggestionsOpen(false);
                                return;
                              }
                              const result = await fetchPlaceSuggestions(value);
                              if (result.success) {
                                setStreetViewSuggestions(result.predictions || []);
                                setStreetViewSuggestionsOpen(true);
                              } else {
                                toast.error(result.error || 'Failed to fetch place suggestions');
                              }
                            }}
                            placeholder="City, address, or landmark"
                            className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground"
                          />
                          {streetViewSuggestionsOpen && streetViewSuggestions.length > 0 && (
                            <div className="absolute z-40 mt-1 w-full max-h-48 overflow-auto rounded-lg border border-border bg-popover shadow-lg text-sm">
                              {streetViewSuggestions.map((p) => (
                                <button
                                  key={p.place_id}
                                  type="button"
                                  className="block w-full text-left px-3 py-2 hover:bg-muted"
                                  onClick={async () => {
                                    setStreetViewPlaceQuery(p.description);
                                    setStreetViewSuggestionsOpen(false);
                                    const details = await fetchPlaceDetails(p.place_id);
                                    if (!details.success || details.lat == null || details.lng == null) {
                                      toast.error(details.error || 'Failed to load place details');
                                      return;
                                    }
                                    setStreetViewLat(String(details.lat));
                                    setStreetViewLng(String(details.lng));
                                    setStreetViewSelectedPlaceName(details.formatted_address || p.description);
                                  }}
                                >
                                  {p.description}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        {streetViewSelectedPlaceName && (
                          <p className="text-xs text-muted-foreground">
                            Selected:{' '}
                            <span className="font-medium text-foreground">{streetViewSelectedPlaceName}</span>
                          </p>
                        )}
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setStreetViewMapOpen(true)}
                            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border border-border bg-muted hover:bg-muted/80"
                          >
                            <Globe className="w-3.5 h-3.5" />
                            Refine on map
                          </button>
                          <span className="text-[11px] text-muted-foreground">
                            Current: {streetViewLat && streetViewLng ? `${streetViewLat}, ${streetViewLng}` : 'not set'}
                          </span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="space-y-2">
                      <label className="block text-xs text-muted-foreground mb-1">Panorama ID (pano)</label>
                      <input
                        type="text"
                        value={streetViewPanoId}
                        onChange={(e) => setStreetViewPanoId(e.target.value)}
                        placeholder="Paste Google Street View pano ID"
                        className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground font-mono"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        Use when you have a specific Google Street View panorama ID (from Maps URL). Location search is
                        ignored in this mode.
                      </p>
                    </div>
                  )}

                  {/* Advanced coordinates (optional) */}
                  {streetViewMode === 'location' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                      <div>
                        <label className="block text-[11px] text-muted-foreground mb-1">Latitude (-90 to 90)</label>
                        <input
                          type="number"
                          step="any"
                          min={-90}
                          max={90}
                          value={streetViewLat}
                          onChange={(e) => setStreetViewLat(e.target.value)}
                          placeholder="e.g. 28.6139"
                          className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-xs text-foreground"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] text-muted-foreground mb-1">Longitude (-180 to 180)</label>
                        <input
                          type="number"
                          step="any"
                          min={-180}
                          max={180}
                          value={streetViewLng}
                          onChange={(e) => setStreetViewLng(e.target.value)}
                          placeholder="e.g. 77.2090"
                          className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-xs text-foreground"
                        />
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Heading (0–360)</label>
                      <input
                        type="number"
                        min={0}
                        max={360}
                        value={streetViewHeading}
                        onChange={(e) => setStreetViewHeading(Number(e.target.value) || 0)}
                        className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Pitch (-90–90)</label>
                      <input
                        type="number"
                        min={-90}
                        max={90}
                        value={streetViewPitch}
                        onChange={(e) => setStreetViewPitch(Number(e.target.value) || 0)}
                        className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">FOV (10–120)</label>
                      <input
                        type="number"
                        min={10}
                        max={120}
                        value={streetViewFov}
                        onChange={(e) => setStreetViewFov(Number(e.target.value) || 90)}
                        className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Tile size</label>
                      <select
                        value={streetViewSize}
                        onChange={(e) => setStreetViewSize(e.target.value)}
                        className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground"
                      >
                        <option value="640x640">640×640</option>
                        <option value="1024x1024">1024×1024</option>
                        <option value="2048x2048">2048×2048</option>
                        <option value="4096x4096">4096×4096</option>
                      </select>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => void applyStreetViewSkybox()}
                    disabled={
                      isReadOnly ||
                      fetchingStreetView ||
                      !chapterId?.trim() ||
                      !topicId?.trim() ||
                      (streetViewMode === 'location' &&
                        (Number.isNaN(parseFloat(streetViewLat)) ||
                          Number.isNaN(parseFloat(streetViewLng)) ||
                          parseFloat(streetViewLat) < -90 ||
                          parseFloat(streetViewLat) > 90 ||
                          parseFloat(streetViewLng) < -180 ||
                          parseFloat(streetViewLng) > 180)) ||
                      (streetViewMode === 'pano' && !streetViewPanoId.trim())
                    }
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-foreground font-semibold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {fetchingStreetView ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Fetching & stitching...
                      </>
                    ) : (
                      <>
                        <Globe className="w-5 h-5" />
                        Fetch & set as skybox
                      </>
                    )}
                  </button>
                  <p className="text-xs text-muted-foreground">
                    Imagery is fetched via the backend and stitched into a 360° equirectangular skybox. No API key is exposed in the browser.
                  </p>
                  <StreetViewMapPicker
                    open={streetViewMapOpen}
                    onOpenChange={setStreetViewMapOpen}
                    initialCenter={
                      streetViewLat && streetViewLng
                        ? { lat: parseFloat(streetViewLat), lng: parseFloat(streetViewLng) }
                        : undefined
                    }
                    onSelectLocation={(lat, lng) => {
                      setStreetViewLat(String(lat));
                      setStreetViewLng(String(lng));
                    }}
                  />
                  </>)}
                </div>
              ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* LEFT: Prompt Input */}
                <div className="lg:col-span-2">
                  <div className="bg-black/60 backdrop-blur-md rounded-2xl border border-border p-5">
                    <div className="flex items-center justify-between mb-4">
                      <label className="flex items-center gap-2 text-sm font-semibold text-foreground">
                        <Sparkles className="w-5 h-5 text-primary" />
                        Scene Description
                      </label>
                      <button
                        disabled={isReadOnly}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                                 text-violet-400 bg-violet-500/10 hover:bg-violet-500/20
                                 rounded-lg border border-violet-500/30
                                 transition-all duration-200 disabled:opacity-50"
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                        AI Enhance
                      </button>
                    </div>
                    <textarea
                      value={localPrompt}
                      onChange={(e) => handlePromptChange(e.target.value)}
                      disabled={isReadOnly || generating}
                      placeholder="Describe your 3D environment in vivid detail... 

Example: A mystical ancient library with towering bookshelves reaching into darkness, floating candles casting warm golden light..."
                      rows={4}
                      className="w-full bg-muted border border-border rounded-xl
                               px-4 py-3.5 text-foreground placeholder:text-muted-foreground text-sm leading-relaxed
                               focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/40
                               disabled:opacity-50 disabled:cursor-not-allowed resize-none
                               transition-all duration-200"
                    />
                  </div>
                </div>
                
                {/* RIGHT: Style & Generate */}
                <div className="space-y-4">
                  {/* Style Selector */}
                  <div className="bg-black/60 backdrop-blur-md rounded-2xl border border-emerald-500/20 p-4">
                    <label className="flex items-center gap-2 text-xs font-semibold text-foreground mb-3">
                      <span className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.6)]" />
                      BlockadeLabs Style
                    </label>
                    
                    {/* Style Preview */}
                    {selectedStyle && (selectedStyle.image_jpg || selectedStyle.image) && (
                      <div className="overflow-hidden rounded-xl border border-white/10 mb-3 shadow-lg">
                        <div className="relative">
                          <img
                            src={selectedStyle.image_jpg || selectedStyle.image}
                            alt={selectedStyle.name}
                            className="w-full h-20 object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                          <div className="absolute bottom-0 left-0 right-0 p-2">
                            <p className="text-xs font-semibold text-foreground truncate">{selectedStyle.name}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Dropdown */}
                    {loadingStyles ? (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground py-3">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading styles...
                      </div>
                    ) : (
                      <div className="relative">
                        <select
                          value={selectedStyle?.id ?? ''}
                          onChange={handleStyleChange}
                          disabled={isReadOnly || generating}
                          className="w-full appearance-none bg-muted border border-emerald-500/20 
                                   rounded-xl px-4 py-2.5 pr-10 text-sm text-foreground
                                   focus:outline-none focus:ring-2 focus:ring-emerald-500/30
                                   disabled:opacity-50 transition-all cursor-pointer"
                        >
                          <option value="" disabled>Select a style...</option>
                          {skyboxStyles.map((style) => (
                            <option key={style.id} value={style.id}>
                              {style.name}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-400/60 pointer-events-none" />
                      </div>
                    )}
                  </div>
                  
                  {/* Generate Button */}
                  <button
                    onClick={handleGenerateSkybox}
                    disabled={isReadOnly || generating || !localPrompt.trim() || !selectedStyle}
                    className={`w-full flex items-center justify-center gap-3 px-6 py-4 
                             text-base font-bold uppercase tracking-wider rounded-2xl
                             transition-all duration-300 shadow-xl
                             ${generating
                               ? 'bg-muted cursor-not-allowed'
                               : 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 hover:shadow-[0_0_40px_rgba(6,182,212,0.5)] hover:-translate-y-1 active:translate-y-0'
                             }
                             text-foreground disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-xl`}
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Generating...</span>
                      </>
                    ) : currentSkyboxUrl ? (
                      <>
                        <RefreshCw className="w-5 h-5" />
                        <span>Regenerate</span>
                      </>
                    ) : (
                      <>
                        <Play className="w-5 h-5" />
                        <span>Generate</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
